'use client'

import { useEffect, useRef } from 'react'
import {
  trackCalculadoraAberta,
  trackCalculadoraConcluida,
  type CalculatorType,
} from '@/lib/analytics'
import { pushRecentCalc } from '@/lib/recent-calcs'

const TIPO_LABEL: Partial<Record<CalculatorType, string>> = {
  margem_lucro: 'Margem de lucro',
  preco_hora: 'Preço por hora',
  precificacao: 'Preço de venda',
  faturamento: 'Limite de faturamento',
  fluxo_caixa: 'Fluxo de caixa',
  calendario_das: 'DAS',
  ponto_equilibrio: 'Ponto de equilíbrio',
  quanto_sobra: 'Quanto sobra de verdade',
  custo_funcionario: 'Custo de funcionário',
}

/**
 * Telemetria GA4 + Postgres para calculadoras.
 * @param completed — true quando o usuário preencheu inputs mínimos e viu resultado.
 * @param resultSummary — resumo curto pra “últimos 3 cálculos” no localStorage.
 */
export function useCalcTelemetry(
  tipo: CalculatorType,
  completed: boolean,
  resultSummary?: string,
) {
  const opened = useRef(false)
  const done = useRef(false)

  useEffect(() => {
    if (opened.current) return
    opened.current = true
    trackCalculadoraAberta(tipo)
  }, [tipo])

  useEffect(() => {
    if (!completed || done.current) return
    done.current = true
    trackCalculadoraConcluida(tipo)
  }, [completed, tipo])

  // Persiste/atualiza o resumo conforme o usuário refina os números
  // (pushRecentCalc substitui a entrada da mesma calculadora).
  useEffect(() => {
    if (!completed) return
    pushRecentCalc({
      source: tipo,
      title: TIPO_LABEL[tipo] ?? tipo,
      summary: resultSummary?.slice(0, 80) || 'Cálculo concluído',
    })
  }, [completed, tipo, resultSummary])
}

/** Slug da rota → tipo interno de analytics. */
export const CALC_SLUG_TO_TYPE: Record<string, CalculatorType> = {
  'margem-de-lucro': 'margem_lucro',
  'preco-por-hora': 'preco_hora',
  'preco-de-venda': 'precificacao',
  'limite-faturamento-mei': 'faturamento',
  'fluxo-de-caixa': 'fluxo_caixa',
  'das': 'calendario_das',
  'ponto-de-equilibrio': 'ponto_equilibrio',
  // Tipo próprio: quando compartilhava 'margem_lucro' com a margem, os dois cálculos
  // se sobrescreviam no localStorage e o "Refazer" mandava pra calculadora errada.
  'quanto-sobra-de-verdade': 'quanto_sobra',
}
